import { Box, HStack, Text, keyframes } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

/**
 * Avancement de l'analyse : une jauge, l'étape en cours, le temps écoulé.
 *
 * Le pipeline ne renvoie RIEN avant d'avoir fini — une requête, une réponse
 * (voir /analyze-new) — donc il n'y a aucune progression réelle à afficher.
 * La jauge n'en invente pas : son remplissage suit une asymptote du temps
 * écoulé et PLAFONNE à 92 %, de sorte qu'elle ne se présente jamais comme
 * terminée avant de l'être. Elle n'affiche aucun pourcentage, qui serait un
 * chiffre inventé ; le seul nombre montré, les secondes, est mesuré.
 *
 * Les durées viennent des temps réellement enregistrés (processingTimeSeconds) :
 * 1 à 3 s pour 2 à 7 bandelettes, 5 à 12 s pour 23 à 41, et bien davantage au
 * premier lancement après un redémarrage du service, qui charge les modèles
 * (188 s mesurées une fois) — d'où le seul texte d'appoint, au-delà d'une minute.
 */
interface AnalysisStage {
  /** Secondes écoulées à partir desquelles l'étape devient l'étape courante. */
  at: number
  label: string
}

// Chaque libellé nomme une opération que le service exécute vraiment. Les
// étapes intermédiaires (recalage en perspective, densitométrie, consensus de
// plaque) existent bien dans le pipeline mais ne sont pas nommées ici : elles
// défilaient trop vite pour être lues et noyaient les deux seules qui parlent
// à l'opérateur, la détection et la lecture des identifiants.
const STAGES_IMAGE: AnalysisStage[] = [
  { at: 0, label: 'Lecture de la plaque' },
  { at: 2, label: 'Détection des bandelettes' },
  { at: 6, label: 'Reconnaissance des identifiants' },
  { at: 10, label: 'Interprétation' },
]

// Un PDF est rastérisé page par page puis empilé en une seule image avant toute
// détection : la plaque est littéralement reconstruite depuis le rapport, et
// c'est l'étape la plus lourde sur les gros rapports multi-pages.
const STAGES_PDF: AnalysisStage[] = [
  { at: 0, label: 'Décodage du rapport' },
  { at: 2, label: 'Reconstruction de la plaque' },
  { at: 7, label: 'Détection des bandelettes' },
  { at: 12, label: 'Reconnaissance des identifiants' },
  { at: 17, label: 'Interprétation' },
]

/** Constante de temps de l'asymptote, en secondes : ~70 % à 10 s. */
const FILL_TAU = 8
/** Plafond : la jauge n'atteint 100 % qu'à l'arrivée de la réponse. */
const FILL_CAP = 0.92

export interface AnalysisProgressState {
  /** Secondes entières écoulées depuis le lancement, mesurées. */
  elapsed: number
  label: string
  /** Remplissage de la jauge, 0 à FILL_CAP. */
  fill: number
}

/**
 * Pilote l'avancement. Un seul minuteur, dans le parent, pour que le bouton et
 * la jauge nomment forcément la même étape.
 */
export const useAnalysisProgress = (
  isActive: boolean,
  isPdf: boolean,
  isReanalysis = false,
): AnalysisProgressState => {
  const [elapsed, setElapsed] = useState(0)

  const stages = useMemo(() => {
    const base = isPdf ? STAGES_PDF : STAGES_IMAGE
    // Une relance repart du fichier déjà stocké : il n'y a rien à envoyer.
    if (!isReanalysis) return base
    return [{ ...base[0], label: 'Relecture de la plaque' }, ...base.slice(1)]
  }, [isPdf, isReanalysis])

  useEffect(() => {
    if (!isActive) {
      setElapsed(0)
      return
    }
    // Date de départ figée : un intervalle qui s'incrémente dérive quand
    // l'onglet passe en arrière-plan (le navigateur étire les timers).
    // Compté en secondes entières, une fois par seconde : ce minuteur vit dans
    // l'espace de travail, dont l'arbre est énorme, et chaque tic le rend à
    // nouveau — inutile d'en faire plus que ce que l'affichage montre.
    const startedAt = Date.now()
    setElapsed(0)
    const id = window.setInterval(
      () => setElapsed(Math.round((Date.now() - startedAt) / 1000)),
      1000,
    )
    return () => window.clearInterval(id)
  }, [isActive])

  return useMemo(() => {
    let current = stages[0]
    stages.forEach((stage) => {
      if (elapsed >= stage.at) current = stage
    })
    return {
      elapsed,
      label: current.label,
      fill: FILL_CAP * (1 - Math.exp(-elapsed / FILL_TAU)),
    }
  }, [elapsed, stages])
}

// Reflet qui balaie la jauge : c'est lui qui donne le mouvement, la largeur ne
// bougeant qu'une fois par seconde.
const sheen = keyframes`
  0% { transform: translateX(-100%) }
  100% { transform: translateX(300%) }
`

export default function AnalysisProgress({ progress }: { progress: AnalysisProgressState }) {
  const { label, elapsed, fill } = progress

  return (
    <Box
      bg="white"
      borderRadius="xl"
      shadow="lg"
      border="1px solid"
      borderColor="gray.100"
      px={{ base: 4, md: 5 }}
      py={4}
    >
      <HStack justify="space-between" align="baseline" mb={2.5} spacing={3}>
        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
          {label}
        </Text>
        <Text
          fontSize="xs"
          color="gray.400"
          fontWeight="600"
          flexShrink={0}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {elapsed} s
        </Text>
      </HStack>

      <Box h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
        <Box
          h="100%"
          borderRadius="full"
          position="relative"
          overflow="hidden"
          bgGradient="linear(to-r, teal.400, cyan.400)"
          style={{ width: `${Math.round(fill * 100)}%` }}
          transition="width 0.9s cubic-bezier(0.22, 1, 0.36, 1)"
        >
          <Box
            position="absolute"
            inset={0}
            bgGradient="linear(to-r, transparent, whiteAlpha.700, transparent)"
            animation={`${sheen} 1.6s ease-in-out infinite`}
          />
        </Box>
      </Box>

      {elapsed >= 60 && (
        <Text fontSize="xs" color="gray.400" mt={2.5}>
          Chargement des modèles — ne rafraîchissez pas la page.
        </Text>
      )}
    </Box>
  )
}
